import { AnyObject } from '@store/types';
import React from 'react';
import { v4 as uuid } from 'uuid';
import some from 'lodash/some';

import {
    EntryTypeFieldName,
    EntryTypeFieldType,
    ReferralCreateData,
    ReferralData,
    ReferralStatus,
    ReferralUpdateForm,
    RenderedServiceCreationParams,
    ServiceStatus,
    type ServiceData,
    ServiceCategory,
} from 'chaika-frontend-types/frontend-cms';

import { store } from '@store';
import { ReferralList } from '@store/models/SystemFields/Referral';
import { GroupedListParams } from '@store/abstract/GroupedList';
import { EntryApi } from '@api/EntryApi';
import { MedicalCaseApi } from '@api/MedicalCaseApi';
import { ReferralApi } from '@api/ReferralApi';
import { ReferralAbstractService } from '@store/services/ReferralService';

import type { OptionType } from '@components/Select';
import { FieldTitle, FieldTitleTheme } from '@components/FieldTitle';
import { entryService } from '@patient/services/entry/entryService';
import { entryPageService } from '@patient/ui/shared/store';
import { TeethSelectMenu } from '@components/Blocks/RenderedService/TeethSelectMenu/TeethSelectMenu';
import { ContractorSelect } from '@common/ReferralMenu/CreateReferral/ContractorSelect';
import { resolveContractor } from '@common/ReferralMenu/utils/contractorResolution';

import { renderedServicesService } from './renderedServicesService';
import { dentalMultiSelectServiceTag } from '@services/store/service/constants';
import { translate } from '@chaika/i18n/helper';
import { assertIsDefined } from '@utils/assertIsDefined';

const USAGE_LIMIT_EXCEEDED = 'UsageLimitExceeded';

class ReferralService extends ReferralAbstractService {
    public async create(referrals: ReferralCreateData[], createList?: boolean): Promise<void> {
        const { entry, ehr } = store;

        const hasUrgentReferrals = some(referrals, 'isUrgent');

        await this.createReferral(
            referrals,
            {
                ehrId: ehr.id,
                entryId: entry.id,
                planActualOnDate: entry.creationTime,
            },
            createList,
        );

        const referralList = store.entry.getFieldValue<ReferralList>(EntryTypeFieldType.Referrals);
        const ehrReferralList = store.ehr.fields.getValue<ReferralList>(EntryTypeFieldType.Referrals);

        const newReferrals =
            referrals.length > 0 ? await ReferralApi.getList({ id: referrals.map(({ id }) => id).join(',') }) : [];

        referralList.add(newReferrals);
        ehrReferralList.add(newReferrals);
        if ((entry.ehrFields as AnyObject)[EntryTypeFieldName.Referrals]) {
            const ehrReferralList = entry.ehrFields.get(EntryTypeFieldType.Referrals).getValue() as ReferralList;
            ehrReferralList.add(newReferrals);
        }

        if (!!entry.id) {
            await Promise.all<any>([
                hasUrgentReferrals && entryService.changeUrgency(true),
                // this.reloadReferrals(),
            ]);
        }
    }

    public async update(id: string, params: ReferralUpdateForm): Promise<void> {
        const entry = store.entry;

        // @ts-expect-error
        const { service, ...data } = params;

        await this.updateReferral({
            ehrId: entry.ehrId,
            groupReferralId: id,
            params: data,
            entryId: entry.id,
            withDuration: true,
        });
        const compositeService = params.compositeServices ? { compositeServices: params.compositeServices } : undefined;
        entry.updateReferral(id, !!service ? { service, ...data, compositeService } : { ...data, compositeService });
    }

    public async updateOne(id: string, params: ReferralUpdateForm): Promise<void> {
        const entry = store.entry;

        await this.updateReferral({ ehrId: entry.ehrId, groupReferralId: id, params, entryId: entry.id });
        setTimeout(async () => {
            await this.reloadReferrals();
        }, 100);
    }

    public async add(id: string, addedCount: number, medicalCaseIds: string[]): Promise<void> {
        const entry = store.entry;
        const newIds = await this.addReferrals(id, addedCount, entry.ehrId, medicalCaseIds);

        const referralList = entry.fields.getValue<ReferralList>(EntryTypeFieldType.Referrals);
        referralList.addItems(id, newIds);

        const { ids } = this.getReferral(id);
        const medicalCasesWithReferral = entry.medicalCases.getFilteredValues({ referralIds: ids });
        medicalCasesWithReferral.forEach(medicalCase => {
            medicalCase.addReferrals(newIds);
        });
    }

    public async remove(id: string, removingCount?: number) {
        const { ehrId, id: entryId, medicalCases } = store.entry;

        const removingIds = await this.removeReferrals({ ehrId, entryId }, id, removingCount);
        this.removeFromList(id, removingIds);

        const medicalCasesWithReferral = medicalCases.getFilteredValues({ referralIds: removingIds });
        medicalCasesWithReferral.forEach(medicalCase => {
            medicalCase.removeReferrals(removingIds);
        });
    }

    public getByMedicalCaseId(medicalCaseId: string, filterByStatus?: ReferralStatus): ReferralData[] {
        const referralList = store.entry.getFieldValue<ReferralList>(EntryTypeFieldType.Referrals);
        return referralList.values.filter(({ status }) => (filterByStatus ? status === filterByStatus : true));
    }

    public async removeActiveByMedicalCaseId(_: string): Promise<void> {
        const entry = store.entry;
        // const idsForRemove: string[] = [];
        // const updatePromises: Promise<void>[] = [];
        const referralList = entry.getFieldValue<ReferralList>(EntryTypeFieldType.Referrals);
        referralList.forEach(({ status }, id) => {
            // if (medicalCaseIds.includes(medicalCaseId) && status === ReferralStatus.Active) {
            //     if (medicalCaseIds.length === 1) {
            //         idsForRemove.push(id);
            //     } else {
            //         updatePromises.push(this.update(id, { medicalCaseIds: medicalCaseIds.filter(it => it !== medicalCaseId) }));
            //     }
            // }
        });

        // await Promise.all([...idsForRemove.map(id => this.remove(id)), ...updatePromises]);
    }

    public async executeReferral(
        id: string,
        executedCount?: number,
        withoutCreateRenderedServices?: boolean,
    ): Promise<void> {
        const { entry, app } = store;

        const { ids, count, ...referral } = await this.getReferral(id);
        const executedIds = executedCount && executedCount < count ? ids.slice(0, executedCount) : ids;

        const renderedServices = withoutCreateRenderedServices
            ? []
            : await this.withResolvedContractors(this.getRenderedServicesOfReferral(referral));

        if (!renderedServices) {
            return;
        }

        app.isOnUpdate = true;

        if (referral.isUrgent) {
            await entryService.changeUrgency(true);
        }

        await EntryApi.executeReferrals(entry.id, executedIds);
        // Нельзя обернуть в Promise.all тк на беке будет ошибка "транзакция заблокирована"
        // await updateEntryFieldService.addMedicalCasesIfNotExist(referral.medicalCaseIds);

        await Promise.all(
            renderedServices.map(renderedService =>
                this.createRenderedServicesFromExecutedReferral(executedIds, renderedService),
            ),
        );

        await this.reloadReferrals();

        app.isOnUpdate = false;
    }

    public async revertReply(id: string, medicalCaseId?: string): Promise<void> {
        const { entry } = store;

        const { ids, serviceId } = await this.getReferral(id);

        await EntryApi.removeReferrals(entry.id, ids);
        try {
            await Promise.all([
                renderedServicesService.removeByServiceIdIfExist(serviceId, ids.length),
                !!medicalCaseId && this.detachMedicalCaseAfterRevertReply(medicalCaseId),
            ]);
        } catch (e: any) {
            console.error('Error on remove rendered service', e);
        }

        await this.reloadReferrals();
        await renderedServicesService.updateStore();
    }

    public async detachMedicalCaseAfterRevertReply(medicalCaseId: string): Promise<void> {
        const { entry } = store;

        const medicalCase = await MedicalCaseApi.getById(medicalCaseId);

        if (!medicalCase.addedEntryIds.includes(entry.id)) {
            const medCase = entry.medicalCases.getExisting(medicalCaseId);
            entry.medicalCases.remove(medicalCaseId);
            entry.ehrMedicalCases.add([medCase.dto]);
        }
    }

    public isReferralServiceComposite({ serviceId }: ReferralData): boolean {
        const { priceListStore } = store;

        return priceListStore.isServiceComposite(serviceId);
    }

    public async reloadReferrals(): Promise<void> {
        const entry = store.entry;
        const {
            entry: { fields },
            ehrFields,
        } = await EntryApi.getEntry(entry.id);
        const referral = fields.find(({ name }) => name === EntryTypeFieldName.Referrals);

        if (referral) {
            const { name, value } = referral;
            entry.fields.removeField(name);
            entry.fields.set(name, value);

            if (ehrFields[EntryTypeFieldName.Referrals]) {
                entry.ehrFields.set(EntryTypeFieldName.Referrals, ehrFields[EntryTypeFieldName.Referrals]);
            }
        }
    }

    protected getReferral(id: string): GroupedListParams<ReferralData> {
        const entry = store.entry;

        const referralList = entry.getFieldValue<ReferralList>(EntryTypeFieldType.Referrals);
        return referralList.getExisting(id);
    }

    protected getReferralList(): ReferralList {
        return store.entry.getFieldValue<ReferralList>(EntryTypeFieldType.Referrals);
    }

    private getRenderedServicesOfReferral(referral: ReferralData): RenderedServiceCreationParams[] {
        if (this.isReferralServiceComposite(referral)) {
            assertIsDefined(
                referral.compositeService,
                `Composite referral ${referral.id} carries its composite service`,
            );
            return referral.compositeService.compositeServices.map(({ id: serviceId, plans }) => ({
                serviceId,
                plans,
            }));
        }

        return [
            {
                serviceId: referral.serviceId,
                // @ts-ignore
                plans: referral.service?.plans,
                contractorId: referral.contractorId,
            },
        ];
    }

    private async withResolvedContractors(
        renderedServices: RenderedServiceCreationParams[],
    ): Promise<RenderedServiceCreationParams[] | undefined> {
        const resolvedRenderedServices: RenderedServiceCreationParams[] = [];

        for (const renderedService of renderedServices) {
            const resolution = await this.resolveRenderedServiceContractor(renderedService);

            if (!resolution) {
                return undefined;
            }

            resolvedRenderedServices.push({ ...renderedService, contractorId: resolution.contractorId });
        }

        return resolvedRenderedServices;
    }

    private async resolveRenderedServiceContractor({
        serviceId,
        contractorId,
    }: RenderedServiceCreationParams): Promise<{ contractorId?: string } | undefined> {
        const { serviceStore, authUser } = store;

        if (contractorId || serviceStore.isServiceLaboratory(serviceId)) {
            return { contractorId };
        }

        const resolution = await resolveContractor(serviceId, authUser.legalEntityId, translate('errorOnRsCreated'));

        if (resolution?.kind === 'selectionRequired') {
            const selectedContractorId = await this.selectContractor(serviceId, resolution.contractorIds);

            return selectedContractorId ? { contractorId: selectedContractorId } : undefined;
        }

        return resolution;
    }

    private selectContractor(serviceId: string, contractorIds: string[]): Promise<string | undefined> {
        return new Promise(resolve => {
            const finishSelection = (contractorId?: string) => {
                entryPageService.resetRightContent();
                resolve(contractorId);
            };

            entryPageService.setRightContent({
                children: React.createElement(
                    'div',
                    null,
                    React.createElement(FieldTitle, {
                        title: store.serviceStore.serviceList.get(serviceId)?.name,
                        theme: FieldTitleTheme.BigBold,
                    }),
                    React.createElement(ContractorSelect, {
                        serviceId,
                        contractorIds,
                        onCancel: () => finishSelection(),
                        onSave: finishSelection,
                    }),
                ),
                onClickOutside: () => finishSelection(),
            });
        });
    }

    private async createRenderedServicesFromExecutedReferral(
        executedIds: string[],
        { serviceId, plans, contractorId }: RenderedServiceCreationParams,
    ): Promise<void> {
        const service = store.serviceStore.serviceList.getExisting(serviceId);

        if (service.status === ServiceStatus.Archived) {
            store.app.showPopup({
                title: translate('renderedServiceNotCreaated'),
                description: `${translate('service')} ${service.name} ${translate('isInArchive')}.`,
            });
        } else {
            if (service.category === ServiceCategory.Dentistry) {
                await this.createDentistryRenderedService(service, plans, executedIds.length, contractorId);
            } else {
                const ids = executedIds.map(() => uuid());
                await this.proceedRenderedServicesCreation(ids, service, plans, undefined, contractorId);
            }
        }
    }

    private async createDentistryRenderedService(
        service: ServiceData,
        plans: RenderedServiceCreationParams['plans'],
        count: number,
        contractorId?: string,
    ): Promise<void> {
        const createServices = async (options: OptionType<string>[]) => {
            entryPageService.resetRightContent();
            const isDentalMulti = !!store.serviceStore.serviceList
                .get(service.id)
                ?.tagIds?.includes(dentalMultiSelectServiceTag);

            if (isDentalMulti) {
                await this.proceedRenderedServicesCreation(
                    [uuid()],
                    service,
                    plans,
                    options.map(item => item.value).join(),
                    contractorId,
                );
            } else {
                for (const { value } of options) {
                    await this.proceedRenderedServicesCreation([uuid()], service, plans, value, contractorId);
                }
            }
        };

        entryPageService.setRightContent({
            children: React.createElement(TeethSelectMenu, {
                count,
                serviceId: service.id,
                onCreate: createServices,
            }),
            onClickOutside: () => entryPageService.resetRightContent(),
        });
    }

    private async proceedRenderedServicesCreation(
        ids: string[],
        service: ServiceData,
        plans: RenderedServiceCreationParams['plans'],
        toothNumber?: string,
        contractorId?: string,
    ) {
        const createServices = async (plans?: RenderedServiceCreationParams['plans']) => {
            await renderedServicesService.create({
                ids,
                renderedService: {
                    serviceId: service.id,
                    plans,
                    toothNumber,
                    contractorId,
                },
            });
        };

        store.app.doOrShowError({
            allowMultiAction: true,
            action: () => createServices(plans),
            errorHandler: async (error: any) => {
                if (error.name === USAGE_LIMIT_EXCEEDED) {
                    await createServices();
                }
            },
        });
    }
}

export const referralService = new ReferralService();
